import { useCallback, useLayoutEffect, useState } from "react";
import { applySiteScheme } from "../siteTheme";

/**
 * Reader settings: how big the text is, which face it is set in, and which
 * palette it sits on.
 *
 * Kept on the device, not the account. They describe a screen and a room — a
 * phone in bed wants dark and large, a laptop at a desk does not — so syncing
 * them would carry the wrong choice to the other device.
 */
export type ReaderTheme = "auto" | "light" | "sepia" | "dark" | "night";

export const READER_THEMES: ReaderTheme[] = ["auto", "light", "sepia", "dark", "night"];

/**
 * The reading faces (requirement 0033). Four, and four is the list: a fifth is
 * a decision about what reading here looks like, not an addition.
 *
 * `sans` is Vazirmatn, the interface's own face and the one the stylesheet has
 * named since M0. The three others are the faces e-readers are known for:
 * Literata is Google Play Books', Merriweather is the serif written for
 * screens, and OpenDyslexic is the one every e-reader offers and this did not.
 */
export type ReaderFont = "sans" | "literata" | "merriweather" | "opendyslexic";

export const READER_FONTS: ReaderFont[] = ["sans", "literata", "merriweather", "opendyslexic"];

/**
 * What each face is called, in its own name. Not in `i18n`: a typeface's name
 * is a proper noun and is the same in both interface languages, and a picker
 * that translated it would be naming something the reader cannot then find.
 */
export const FONT_NAMES: Record<ReaderFont, string> = {
  sans: "Vazirmatn",
  literata: "Literata",
  merriweather: "Merriweather",
  opendyslexic: "OpenDyslexic",
};

/**
 * The stack each face resolves to.
 *
 * **Every one of them ends in Vazirmatn**, and that is the rule, not a
 * tidiness. Literata, Merriweather and OpenDyslexic draw no Arabic script at
 * all: a reader who picks Literata for an English book and then opens a Persian
 * one would get whatever the device falls back to, with the joining broken —
 * which is exactly the failure `--font-sans` was written to prevent. The
 * browser falls through per glyph, so one Persian line with Latin digits in it
 * gets Literata's digits and Vazirmatn's letters, which is what is wanted.
 */
export const FONT_STACKS: Record<ReaderFont, string> = {
  sans: '"Vazirmatn", ui-sans-serif, system-ui, sans-serif',
  literata: '"Literata", "Vazirmatn", Georgia, serif',
  merriweather: '"Merriweather", "Vazirmatn", Georgia, serif',
  opendyslexic: '"OpenDyslexic", "Vazirmatn", ui-sans-serif, sans-serif',
};

/** Text sizes in rem, smallest first. 1.15 is what the reader always used. */
export const TEXT_SIZES = [0.95, 1.05, 1.15, 1.3, 1.45, 1.65] as const;

export interface ReaderSettings {
  /** An index into TEXT_SIZES. */
  size: number;
  theme: ReaderTheme;
  font: ReaderFont;
  /** Whether a face shows who says each quote (requirement 0058). */
  speakers: boolean;
  /**
   * Whether a chapter without audio bands the sentence at the reading line
   * (requirement 0068). The web has no reading line yet; it keeps the field so
   * the stored shape is the app's (ADR-0122).
   */
  highlight: boolean;
  /**
   * Whether a chapter with audio opens played or read (requirement 0069). The
   * web has no switch; it keeps the field so the stored shape is the app's.
   */
  mode: ReaderMode;
  /**
   * Auto-play next chapter (requirement 0110): a Listen chapter that ends
   * moves on to the next after a visible countdown (player/autoPlay.ts).
   */
  autoplay: boolean;
}

export const READER_MODES = ["listen", "read"] as const;
export type ReaderMode = (typeof READER_MODES)[number];

export const DEFAULT_SETTINGS: ReaderSettings = {
  size: 2,
  theme: "auto",
  font: "literata",
  speakers: true,
  highlight: true,
  mode: "listen",
  autoplay: false,
};

const KEY = "sageleaf.reader";

/**
 * parseSettings reads what was stored, falling back field by field. Storage
 * outlives the code that wrote it, so a removed theme or a size index past the
 * end must land on a default rather than on an unstyled page.
 */
export function parseSettings(raw: string | null): ReaderSettings {
  let value: Partial<Record<keyof ReaderSettings, unknown>> = {};
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (parsed && typeof parsed === "object") value = parsed;
  } catch {
    // Unreadable is the same as unset.
  }
  const size =
    typeof value.size === "number" &&
    Number.isInteger(value.size) &&
    value.size >= 0 &&
    value.size < TEXT_SIZES.length
      ? value.size
      : DEFAULT_SETTINGS.size;
  const theme = READER_THEMES.includes(value.theme as ReaderTheme)
    ? (value.theme as ReaderTheme)
    : DEFAULT_SETTINGS.theme;
  const font = READER_FONTS.includes(value.font as ReaderFont)
    ? (value.font as ReaderFont)
    : DEFAULT_SETTINGS.font;
  const speakers = typeof value.speakers === "boolean" ? value.speakers : DEFAULT_SETTINGS.speakers;
  const highlight =
    typeof value.highlight === "boolean" ? value.highlight : DEFAULT_SETTINGS.highlight;
  const mode = READER_MODES.includes(value.mode as ReaderMode)
    ? (value.mode as ReaderMode)
    : DEFAULT_SETTINGS.mode;
  const autoplay = typeof value.autoplay === "boolean" ? value.autoplay : DEFAULT_SETTINGS.autoplay;
  return { size, theme, font, speakers, highlight, mode, autoplay };
}

/** Set once `serifDefault` has run on this device; the app's key too. */
const SERIF_DEFAULT_KEY = "sageleaf.reader.serif-default";

/**
 * serifDefault moves stored settings that hold "sans" to Merriweather, once
 * per device (ADR-0199 §2, as the app's ReaderSettingsStore.serifDefault).
 * Every save writes every field, so settings saved before Merriweather became
 * the default (10-22) hold "sans" whether or not anybody chose it: the owner's
 * phone still read in Vazirmatn the next day. A reader who picks Sans again
 * keeps it, because this never runs twice.
 */
export function serifDefault(raw: string | null): string | null {
  return moveFace(raw, "sans", "merriweather");
}

/** Set once `literataDefault` has run on this device; the app's key too. */
const LITERATA_DEFAULT_KEY = "sageleaf.reader.literata-default";

/**
 * literataDefault moves stored settings that hold "merriweather" to Literata,
 * once per device, when Literata became the default (owner, 10-26), for the
 * same reason as serifDefault: a stored face cannot be told from a default
 * written back. A reader who picks Merriweather again keeps it.
 */
export function literataDefault(raw: string | null): string | null {
  return moveFace(raw, "merriweather", "literata");
}

function moveFace(raw: string | null, from: ReaderFont, to: ReaderFont): string | null {
  if (!raw) return raw;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && (parsed as { font?: unknown }).font === from) {
      return JSON.stringify({ ...parsed, font: to });
    }
  } catch {
    // Unreadable stays as it is; parseSettings falls back field by field.
  }
  return raw;
}

function read(): string | null {
  try {
    let raw = localStorage.getItem(KEY);
    // Oldest first: a device that skipped 10-22 moves sans to Merriweather, then on to Literata.
    for (const [key, move] of [
      [SERIF_DEFAULT_KEY, serifDefault],
      [LITERATA_DEFAULT_KEY, literataDefault],
    ] as const) {
      if (localStorage.getItem(key)) continue;
      localStorage.setItem(key, "1");
      const moved = move(raw);
      if (moved !== raw && moved) localStorage.setItem(KEY, moved);
      raw = moved;
    }
    return raw;
  } catch {
    return null;
  }
}

/**
 * useReaderSettings holds the settings and applies them while the reader — or
 * the settings page, as a live preview — is mounted.
 *
 * Applied to the document root rather than to a wrapper, because the body's
 * background, the transport and every sheet are part of the page being themed,
 * and fixed-position elements are not inside any wrapper's box. Removed on
 * unmount: the themes are the reader's, and the library and studio go back to
 * the site's scheme (the header's sun/moon, or the system's).
 */
export function useReaderSettings(): [ReaderSettings, (patch: Partial<ReaderSettings>) => void] {
  const [settings, setSettings] = useState(() => parseSettings(read()));

  const update = useCallback((patch: Partial<ReaderSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  // A layout effect, not a passive one: passive effects run after paint, so
  // arriving painted one frame in the system's theme and leaving painted the
  // library one frame in the reader's. Mobile Safari showed it in the test.
  useLayoutEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(settings));
    } catch {
      // Private browsing or full storage: the choice lasts this visit.
    }
    const root = document.documentElement;
    // "Auto" is the site's own light or dark (siteTheme.ts), which itself
    // follows the system until the header's sun/moon is used.
    if (settings.theme === "auto") applySiteScheme(root);
    else root.dataset.theme = settings.theme;
    root.style.setProperty("--reader-text", `${TEXT_SIZES[settings.size]}rem`);
    root.style.setProperty("--reader-font", FONT_STACKS[settings.font]);
    return () => {
      applySiteScheme(root);
      root.style.removeProperty("--reader-text");
      root.style.removeProperty("--reader-font");
    };
  }, [settings]);

  return [settings, update];
}
